import React, {useEffect, useState} from 'react';
import {GiftSmall} from '@wix/wix-ui-icons-common/on-stage';
import {SummaryDataHooks} from '../AsideSummary/AsideSummary';
import {CodeInputSection} from '../CodeInputSection/CodeInputSection';
import {ILocaleKeys, useLocaleKeys} from '../../../../locale-keys/LocaleKeys';
import {useControllerProps} from '../../Widget/ControllerContext';
import {CheckoutErrorModel} from '../../../../domain/models/checkout/CheckoutError.model';
import {CheckoutErrorCode} from '../../../../domain/utils/errors';
import {AddGiftCardErrorModel} from '../../../../domain/models/checkout/Errors/AddGiftCardError.model';
import {useExperiments} from '@wix/yoshi-flow-editor';
import {SPECS} from '../../../../common/constants';

export enum GiftCardInputDataHooks {
  enterACodeButtonDisabledTooltip = 'GiftCardInput.enterACodeButtonDisabledTooltip',
}

export const GiftCardInput: React.FC<{mobilePosition?: string}> = ({mobilePosition}) => {
  const localeKeys = useLocaleKeys();

  const {
    cartSummary,
    cart,
    applyGiftCard,
    removeGiftCard,
    applyGiftCardError,
    onAddGiftCardSectionOpen,
    isGiftCardUpdating,
  } = useControllerProps().checkoutStore;
  const {experiments} = useExperiments();
  const newCheckoutErrorModels = experiments.enabled(SPECS.NewCheckoutErrorModels);
  const [giftCardCodeError, setGiftCardCodeError] = useState(
    applyGiftCardError
      ? getErrorTextFromGiftCardError(applyGiftCardError, localeKeys, newCheckoutErrorModels)
      : undefined
  );

  useEffect(() => {
    const error = applyGiftCardError ?? cartSummary?.errors?.giftCardError;
    setGiftCardCodeError(error ? getErrorTextFromGiftCardError(error, localeKeys, newCheckoutErrorModels) : undefined);
  }, [cartSummary, applyGiftCardError, localeKeys, newCheckoutErrorModels]);

  const handleCodeInputOpen = () => {
    onAddGiftCardSectionOpen(mobilePosition);
  };

  const handleApplyGiftCard = (giftCardCode: string) => {
    void applyGiftCard(giftCardCode, mobilePosition);
  };

  const handleRemoveCode = () => {
    void removeGiftCard(mobilePosition);
  };

  const enterACodeButtonDisabledTooltipData = {
    dataHook: GiftCardInputDataHooks.enterACodeButtonDisabledTooltip,
    content: localeKeys.checkout.paymentDetails.redeemGiftCard.cantPurchaseGC.tooltip(),
  };
  return (
    <CodeInputSection
      dataHook={SummaryDataHooks.giftCardSectionInput}
      enterACodeButtonText={localeKeys.checkout.paymentDetails.redeemGiftCard.checkbox.label()}
      onCodeInputOpen={handleCodeInputOpen}
      applyCodeText={localeKeys.checkout.paymentDetails.enterGiftCard.CTA()}
      removeCodeText={localeKeys.checkout.paymentDetails.enterGiftCard.remove()}
      codeValue={cart.paymentInfo.giftCard?.obfuscatedCode}
      isLoading={!cartSummary}
      onApplyCode={handleApplyGiftCard}
      onRemoveCode={handleRemoveCode}
      error={giftCardCodeError}
      icon={<GiftSmall />}
      placeholder={localeKeys.checkout.paymentDetails.enterGiftCard.placeholder()}
      isUpdating={isGiftCardUpdating}
      isEnterACodeButtonDisabled={cart.hasGiftCardItems}
      enterACodeButtonDisabledTooltipData={enterACodeButtonDisabledTooltipData}
      ariaLabel={localeKeys.checkout.giftCard.inputCode.ariaLabel()}
    />
  );
};

const getErrorTextFromGiftCardError = (
  applyGiftCardError: CheckoutErrorModel | AddGiftCardErrorModel,
  localeKeys: ILocaleKeys,
  newCheckoutErrorModels: boolean
): string => {
  if (newCheckoutErrorModels) {
    return (applyGiftCardError as AddGiftCardErrorModel).description;
  }
  switch ((applyGiftCardError as CheckoutErrorModel)?.code) {
    case CheckoutErrorCode.GIFT_CARD_EXPIRED:
      return localeKeys.checkout.paymentDetails.enterGiftCard.error.cardExpired();
    case CheckoutErrorCode.GIFT_CARD_DISABLED:
      return localeKeys.checkout.paymentDetails.enterGiftCard.error.cardDisabled();
    case CheckoutErrorCode.GIFT_CARD_IS_EMPTY:
      return localeKeys.checkout.paymentDetails.enterGiftCard.error.noBalance();
    case CheckoutErrorCode.GIFT_CARD_SERVICE_UNAVAILABLE:
      return localeKeys.checkout.paymentDetails.enterGiftCard.error.serviceDown.inline();
    case CheckoutErrorCode.GIFT_CARD_CURRENCY_INVALID:
      return localeKeys.checkout.paymentDetails.enterGiftCard.error.wrongCurrency();
    case CheckoutErrorCode.GIFT_CARD_CODE_INVALID:
      return localeKeys.checkout.paymentDetails.enterGiftCard.error.invalidCardNumber();
    case CheckoutErrorCode.GIFT_CARD_GENERAL_ERROR:
    case CheckoutErrorCode.GIFT_CARD_UNSUPPORTED_STATUS:
    case CheckoutErrorCode.GIFT_CARD_INSUFFICIENT_BALANCE:
    default:
      return localeKeys.checkout.paymentDetails.enterGiftCard.error.serviceDown.inline();
  }
};
